import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import SearchBox from './SearchBox';
import ImportPreview from './ImportPreview';
import EditGroupModal from './EditGroupModal';
import { fetchDonors as fetchSupabaseDonors, supabaseDonorToAppFormat } from '../lib/supabaseDb';
import { authFetch, authPost } from '../lib/authFetch';
import './PeopleGrid.css';
import './ListView.css'; // For inline list view styles

const GROUP_EXAMPLE_PROMPTS = [
  "Donors",
  "Customers",
  "Clients",
  "Leads",
  "Prospects",
  "Volunteers"
];

const LIST_EXAMPLE_PROMPTS = [
  "Top givers",
  "Needs follow-up",
  "Recent contacts",
  "Hot leads",
  "VIP tier",
  "Lapsed"
];

const TABLE_EXAMPLE_PROMPTS = [
  "Name, giving amount, last contact date",
  "Name, company, interests, next steps",
  "Name, tier, sentiment, lifetime value",
  "Name, email, phone, address",
  "Name, relationship strength, key notes"
];

function PeopleGrid({ dataMode, onSelectDonor, conversationHistory, onAddMessage, onAction, user, refreshTrigger, groups = [], activeGroupId, onCreateGroup, onUpdateGroup, onSelectGroup, savedLists = [], activeListId, onSaveList, onSelectList, onDeleteList, showAllPeople = false, onAddPerson, onMobileSheetChange }) {
  const [donors, setDonors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDonors, setFilteredDonors] = useState([]);
  const [highlightField, setHighlightField] = useState(null); // 'amount', 'nextMeeting', 'lastContact', etc.
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, donorName }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // donor name to confirm deletion

  // Import state
  const [importPreview, setImportPreview] = useState(null);
  const [isImportLoading, setIsImportLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Group creation state
  const [newGroupName, setNewGroupName] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // List creation state
  const [showListCreation, setShowListCreation] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [listExampleIndex, setListExampleIndex] = useState(0);
  const [isListAnimatingOut, setIsListAnimatingOut] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [showListDropdown, setShowListDropdown] = useState(false);
  const [deleteConfirmList, setDeleteConfirmList] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showMobileListSheet, setShowMobileListSheet] = useState(false);
  const [showAllPeopleSheet, setShowAllPeopleSheet] = useState(false);
  const [showViewTypeDropdown, setShowViewTypeDropdown] = useState(false);
  const [showTableCreation, setShowTableCreation] = useState(false);
  const [newTablePrompt, setNewTablePrompt] = useState('');
  const [isCreatingTable, setIsCreatingTable] = useState(false);
  const [tableExampleIndex, setTableExampleIndex] = useState(0);
  const [isTableAnimatingOut, setIsTableAnimatingOut] = useState(false);

  // Contextual hints state
  const [dismissedHints, setDismissedHints] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('knowroom-dismissed-hints') || '{}');
    } catch {
      return {};
    }
  });

  const dismissHint = (hintKey) => {
    const newDismissed = { ...dismissedHints, [hintKey]: true };
    setDismissedHints(newDismissed);
    localStorage.setItem('knowroom-dismissed-hints', JSON.stringify(newDismissed));
  };

  // Inline table editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, colName, donorName }
  const [editingValue, setEditingValue] = useState('');
  const [updatingCell, setUpdatingCell] = useState(null); // { rowIndex, colName } - for rainbow animation

  // Column editing state
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [editingColumnName, setEditingColumnName] = useState('');
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [populatingColumn, setPopulatingColumn] = useState(null); // column name being AI-populated

  // List view state (when viewing a list within a group)
  const [draggedPerson, setDraggedPerson] = useState(null);
  const [draggedFromGroup, setDraggedFromGroup] = useState(null);
  const [dropTargetGroup, setDropTargetGroup] = useState(null);
  const [listGroups, setListGroups] = useState(null); // Local copy of list groups for drag-and-drop

  // Edit group modal state
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);

  // Notify parent when mobile sheet opens/closes (to hide bottom icons)
  useEffect(() => {
    if (onMobileSheetChange) {
      onMobileSheetChange(showMobileListSheet || showAllPeopleSheet);
    }
  }, [showMobileListSheet, showAllPeopleSheet, onMobileSheetChange]);

  // Fetch all donors on mount
  useEffect(() => {
    const fetchDonors = async () => {
      console.log('PeopleGrid fetchDonors called, dataMode:', dataMode, 'user:', user?.id);
      setIsLoading(true);
      try {
        // In real mode with logged-in user, fetch from Supabase
        if (dataMode === 'real' && user?.id) {
          console.log('Fetching from Supabase for user:', user.id);
          const supabaseDonors = await fetchSupabaseDonors(user.id);
          console.log('Supabase returned:', supabaseDonors);
          const donorList = supabaseDonors.map(d => supabaseDonorToAppFormat(d));
          console.log('Formatted donor list:', donorList);
          setDonors(donorList);
          setFilteredDonors(donorList);
          setIsLoading(false);
          return;
        }

        // Otherwise use API
        const response = await authFetch(`/api/donors?mode=${dataMode}`);
        if (response.ok) {
          const data = await response.json();
          const donorList = Object.entries(data.donors || {}).map(([name, details]) => ({
            name,
            ...details
          }));
          setDonors(donorList);
          setFilteredDonors(donorList);
        }
      } catch (error) {
        console.error('Error fetching donors:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDonors();
  }, [dataMode, user, refreshTrigger]);

  // Filter donors based on search and activeGroupId
  useEffect(() => {
    // Don't override if we have a highlight (means AI responded)
    if (highlightField) return;

    let result = donors;

    // First filter by group if activeGroupId is set (unless showing all people)
    if (activeGroupId && !showAllPeople) {
      result = result.filter(donor => donor.groupId === activeGroupId);
    }

    // Then filter by search query
    if (searchQuery.trim()) {
      // Only do local filtering for short queries that look like names
      const query = searchQuery.toLowerCase();
      const looksLikeName = searchQuery.length < 20 && !searchQuery.includes(' my ') && !searchQuery.includes('top') && !searchQuery.includes('show') && !searchQuery.includes('who');
      if (looksLikeName) {
        result = result.filter(donor =>
          donor.name.toLowerCase().includes(query)
        );
      }
    }

    setFilteredDonors(result);
  }, [searchQuery, donors, highlightField, activeGroupId, showAllPeople]);

  // Clear AI message when switching groups
  useEffect(() => {
    setAiMessage(null);
  }, [activeGroupId]);

  // Cycle through example prompts for group creation
  useEffect(() => {
    // Only animate if showing the group creation prompt and user isn't typing
    if (activeGroupId || newGroupName) return;

    const interval = setInterval(() => {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setCurrentExampleIndex(prev => (prev + 1) % GROUP_EXAMPLE_PROMPTS.length);
        setIsAnimatingOut(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [activeGroupId, newGroupName]);

  // Handle creating a new group
  const handleCreateGroupSubmit = async () => {
    const groupName = newGroupName.trim();
    if (!groupName || !onCreateGroup) return;

    setIsCreatingGroup(true);
    try {
      const newGroup = await onCreateGroup(groupName);
      if (newGroup && onSelectGroup) {
        onSelectGroup(newGroup.id);
      }
      setNewGroupName('');
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Cycle through list example prompts
  useEffect(() => {
    if (!showListCreation || newListName) return;

    const interval = setInterval(() => {
      setIsListAnimatingOut(true);
      setTimeout(() => {
        setListExampleIndex(prev => (prev + 1) % LIST_EXAMPLE_PROMPTS.length);
        setIsListAnimatingOut(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [showListCreation, newListName]);

  // Cycle through table example prompts
  useEffect(() => {
    if (!showTableCreation || newTablePrompt) return;

    const interval = setInterval(() => {
      setIsTableAnimatingOut(true);
      setTimeout(() => {
        setTableExampleIndex(prev => (prev + 1) % TABLE_EXAMPLE_PROMPTS.length);
        setIsTableAnimatingOut(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [showTableCreation, newTablePrompt]);

  // Handle creating a new list - uses AI to organize people
  const handleCreateListSubmit = async () => {
    const listName = newListName.trim();
    // Allow creating lists without activeGroupId (for "All" view)
    if (!listName || !onSaveList) return;

    setIsCreatingList(true);
    try {
      // Call AI to organize people (in group or all people if no groupId)
      const response = await authPost('/api/organize-list', {
        query: listName,
        mode: dataMode,
        groupId: activeGroupId || null
      });

      if (!response.ok) throw new Error('Failed to organize list');

      const data = await response.json();

      // Create the list with AI-organized groups
      const newList = {
        id: `list-${Date.now()}`,
        name: listName,
        organizationPrompt: listName,
        groups: data.groups || []
      };

      const savedList = await onSaveList(newList, activeGroupId || null);
      setNewListName('');
      setShowListCreation(false);

      // Select the new list to view it
      if (savedList && onSelectList) {
        onSelectList(savedList.id);
      }
    } catch (error) {
      console.error('Failed to create list:', error);
    } finally {
      setIsCreatingList(false);
    }
  };

  // Handle creating a new table - uses AI to extract columns from narratives
  const handleCreateTableSubmit = async () => {
    const prompt = newTablePrompt.trim();
    if (!prompt || !activeGroupId) return;

    setIsCreatingTable(true);
    try {
      // Server fetches members from Supabase, generates table via AI, and saves it
      const response = await authPost('/api/tables', {
        prompt,
        groupId: activeGroupId
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create table');
      }

      const data = await response.json();
      setNewTablePrompt('');
      setShowTableCreation(false);

      // Select the new table to view it
      if (data.table && onSelectList) {
        onSelectList(data.table.id);
      }
    } catch (error) {
      console.error('Failed to create table:', error);
      alert(error.message || 'Failed to create table');
    } finally {
      setIsCreatingTable(false);
    }
  };

  // Get lists for current group (or "All" lists when showAllPeople)
  const groupLists = savedLists.filter(list => list.groupId === activeGroupId);
  const allPeopleLists = savedLists.filter(list => list.groupId === null);
  const currentLists = showAllPeople ? allPeopleLists : groupLists;

  // Get active list and sync local groups for drag-and-drop
  const activeList = currentLists.find(l => l.id === activeListId) || savedLists.find(l => l.id === activeListId);

  // Sync listGroups when activeList changes and handle auto-refresh for lists
  // Note: Table refresh is handled by App.js refreshAllStaleLists to avoid blink
  const activeListGroupsJson = activeList?.groups ? JSON.stringify(activeList.groups) : null;
  useEffect(() => {
    // For tables, just ensure listGroups is null (no list groups to show)
    if (activeList && activeList.type === 'table') {
      setListGroups(prev => prev === null ? prev : null);
      return;
    }

    // Sync local listGroups with activeList groups for display
    // Note: List refresh is handled by App.js refreshAllStaleLists to avoid blink
    if (activeList && activeList.groups) {
      setListGroups(activeList.groups);
    } else {
      setListGroups(null);
    }
  }, [activeListId, activeListGroupsJson]);

  // Filter table rows based on search query (for inline filtering in table view)
  const filteredTableRows = useMemo(() => {
    if (!activeList || activeList.type !== 'table' || !activeList.rows) return [];
    if (!searchQuery.trim()) return activeList.rows;

    const query = searchQuery.toLowerCase();
    // Only filter for simple name-like queries
    const looksLikeName = searchQuery.length < 20 && !searchQuery.includes(' my ') &&
      !searchQuery.includes('top') && !searchQuery.includes('show') && !searchQuery.includes('who');

    if (!looksLikeName) return activeList.rows;

    return activeList.rows.filter(row =>
      row.Name && row.Name.toLowerCase().includes(query)
    );
  }, [activeList, searchQuery]);

  // Filter list groups based on search query (for inline filtering in list view)
  const filteredListGroups = useMemo(() => {
    if (!listGroups || listGroups.length === 0) return listGroups;
    if (!searchQuery.trim()) return listGroups;

    const query = searchQuery.toLowerCase();
    // Only filter for simple name-like queries
    const looksLikeName = searchQuery.length < 20 && !searchQuery.includes(' my ') &&
      !searchQuery.includes('top') && !searchQuery.includes('show') && !searchQuery.includes('who');

    if (!looksLikeName) return listGroups;

    // Filter people within each group, keeping groups that have matches
    return listGroups
      .map(group => ({
        ...group,
        people: (group.people || []).filter(person =>
          person.toLowerCase().includes(query)
        )
      }))
      .filter(group => group.people.length > 0);
  }, [listGroups, searchQuery]);

  // Inline table cell editing handlers
  const handleCellClick = (e, rowIndex, colName, donorName, currentValue) => {
    // Don't edit the Name column - that opens the donor panel
    if (colName === 'Name') return;
    e.stopPropagation();
    setEditingCell({ rowIndex, colName, donorName });
    setEditingValue(currentValue || '');
  };

  const handleCellKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await saveCellEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const saveCellEdit = async () => {
    if (!editingCell) return;

    const { rowIndex, colName, donorName } = editingCell;
    let newValue = editingValue.trim();

    // Format dollar amounts if column is money-related
    const isMoneyColumn = /amount|giving|donation|gift|contributed/i.test(colName);
    if (isMoneyColumn && newValue) {
      // Remove any existing formatting
      const numericValue = newValue.replace(/[$,]/g, '');
      // If it's a valid number, format it
      if (!isNaN(numericValue) && numericValue !== '') {
        const num = parseFloat(numericValue);
        newValue = '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
    }

    // Update the local table data
    if (activeList && activeList.rows) {
      const updatedRows = [...activeList.rows];
      updatedRows[rowIndex] = { ...updatedRows[rowIndex], [colName]: newValue };

      // Save the updated list
      const updatedList = { ...activeList, rows: updatedRows };
      if (onSaveList) {
        onSaveList(updatedList);
      }
    }

    // Set updating state for rainbow animation
    setUpdatingCell({ rowIndex, colName });
    setEditingCell(null);
    setEditingValue('');

    // Update the person's narrative in the background
    if (donorName && newValue) {
      try {
        // Format as a clear note that the AI will save
        const noteText = `${donorName}'s ${colName.toLowerCase()} is ${newValue}`;
        console.log('Saving table edit to narrative:', noteText);
        const response = await authPost('/api/people-search', {
          query: noteText,
          mode: dataMode,
          focusedDonor: donorName,
          groupId: activeGroupId
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Narrative update response:', data);
        } else {
          console.error('Narrative update failed:', response.status);
        }
      } catch (err) {
        console.error('Failed to update narrative:', err);
      }
    }

    // Clear updating state after animation completes
    setTimeout(() => {
      setUpdatingCell(null);
    }, 1500);
  };

  const handleCellBlur = () => {
    // Save on blur as well
    saveCellEdit();
  };

  // Column header editing handlers
  const handleColumnClick = (colIndex, colName) => {
    if (colName === 'Name') return; // Don't allow editing the Name column
    setEditingColumnIndex(colIndex);
    setEditingColumnName(colName);
  };

  const handleColumnKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveColumnEdit();
    } else if (e.key === 'Escape') {
      setEditingColumnIndex(null);
      setEditingColumnName('');
    }
  };

  const saveColumnEdit = async () => {
    if (editingColumnIndex === null || !activeList) return;

    const oldName = activeList.columns[editingColumnIndex];
    const newName = editingColumnName.trim();

    if (!newName || newName === oldName) {
      setEditingColumnIndex(null);
      setEditingColumnName('');
      return;
    }

    // Update columns array
    const updatedColumns = [...activeList.columns];
    updatedColumns[editingColumnIndex] = newName;

    // Update all rows to use new column name
    const updatedRows = activeList.rows.map(row => {
      const newRow = { ...row };
      if (oldName in newRow) {
        newRow[newName] = newRow[oldName];
        delete newRow[oldName];
      }
      return newRow;
    });

    const updatedList = {
      ...activeList,
      columns: updatedColumns,
      rows: updatedRows
    };

    if (onSaveList) {
      await onSaveList(updatedList, activeGroupId);
    }

    setEditingColumnIndex(null);
    setEditingColumnName('');
  };

  const handleDeleteColumn = async (colIndex, colName) => {
    if (!activeList || colName === 'Name') return;

    // Remove column from columns array
    const updatedColumns = activeList.columns.filter((_, i) => i !== colIndex);

    // Remove column data from all rows
    const updatedRows = activeList.rows.map(row => {
      const newRow = { ...row };
      delete newRow[colName];
      return newRow;
    });

    const updatedList = {
      ...activeList,
      columns: updatedColumns,
      rows: updatedRows
    };

    if (onSaveList) {
      await onSaveList(updatedList, activeGroupId);
    }

    setEditingColumnIndex(null);
    setEditingColumnName('');
  };

  const handleAddColumnSubmit = async () => {
    const columnName = newColumnName.trim();
    if (!columnName || !activeList) return;

    // Add new column to columns array
    const updatedColumns = [...activeList.columns, columnName];

    // Add empty values for new column in all rows
    const updatedRows = activeList.rows.map(row => ({
      ...row,
      [columnName]: ''
    }));

    const updatedList = {
      ...activeList,
      columns: updatedColumns,
      rows: updatedRows
    };

    // Save immediately with empty values
    if (onSaveList) {
      await onSaveList(updatedList, activeGroupId);
    }

    setIsAddingColumn(false);
    setNewColumnName('');

    // Now populate the column with AI
    setPopulatingColumn(columnName);
    try {
      const response = await authPost('/api/populate-column', {
        columnName,
        people: updatedRows.map(r => r.Name).filter(Boolean),
        mode: dataMode,
        groupId: activeGroupId
      });

      if (response.ok) {
        const data = await response.json();
        // data.values should be { "Person Name": "value", ... }
        const populatedRows = updatedRows.map(row => ({
          ...row,
          [columnName]: data.values?.[row.Name] || row[columnName] || ''
        }));

        const populatedList = {
          ...activeList,
          columns: updatedColumns,
          rows: populatedRows
        };

        if (onSaveList) {
          await onSaveList(populatedList, activeGroupId);
        }
      }
    } catch (err) {
      console.error('Failed to populate column:', err);
    } finally {
      setPopulatingColumn(null);
    }
  };

  // Drag and drop handlers for list view
  const handleDragStart = (person, groupIndex) => {
    setDraggedPerson(person);
    setDraggedFromGroup(groupIndex);
  };

  const handleDragEnd = () => {
    setDraggedPerson(null);
    setDraggedFromGroup(null);
    setDropTargetGroup(null);
  };

  const handleDragOver = (e, groupIndex) => {
    e.preventDefault();
    if (groupIndex !== draggedFromGroup) {
      setDropTargetGroup(groupIndex);
    }
  };

  const handleDragLeave = () => {
    setDropTargetGroup(null);
  };

  const handleDrop = async (e, targetGroupIndex) => {
    e.preventDefault();
    if (draggedPerson && draggedFromGroup !== null && targetGroupIndex !== draggedFromGroup && listGroups) {
      // Compute new groups
      const newGroups = listGroups.map((group) => ({
        ...group,
        people: [...group.people]
      }));

      // Remove from source group
      newGroups[draggedFromGroup].people = newGroups[draggedFromGroup].people.filter(
        p => p !== draggedPerson
      );

      // Add to target group
      newGroups[targetGroupIndex].people.push(draggedPerson);

      // Update local state
      setListGroups(newGroups);

      // Save changes to Supabase
      if (activeList && onSaveList) {
        const updatedList = {
          ...activeList,
          groups: newGroups
        };
        await onSaveList(updatedList, activeGroupId);
        console.log('Saved drag-and-drop changes to list:', activeList.name);
      }
    }

    setDraggedPerson(null);
    setDraggedFromGroup(null);
    setDropTargetGroup(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!showListDropdown && !showAddDropdown && !showViewTypeDropdown) return;
    const handleClickOutside = () => {
      setShowListDropdown(false);
      setShowAddDropdown(false);
      setShowViewTypeDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showListDropdown, showAddDropdown, showViewTypeDropdown]);

  // Smart search - handles both name filtering and natural language commands
  // When a list is active, uses organize-list to re-organize (same as ListView)
  // BUT if the query is an "add" request, use universal chat instead
  // For simple name searches in tables/lists, just select the person directly
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    const query = searchQuery.trim();

    // Check if this looks like a simple name search (not a command)
    const looksLikeName = query.length < 20 && !query.includes(' my ') &&
      !query.includes('top') && !query.includes('show') && !query.includes('who');

    // For tables: if it's a name search and we have exactly one match, select them
    if (activeList && activeList.type === 'table' && looksLikeName) {
      if (filteredTableRows.length === 1 && filteredTableRows[0].Name) {
        onSelectDonor && onSelectDonor(filteredTableRows[0].Name);
        setSearchQuery('');
        return;
      }
      // If filtering is showing results, don't make API call
      if (filteredTableRows.length > 0 && filteredTableRows.length < (activeList.rows?.length || 0)) {
        return;
      }
    }

    // For lists: if it's a name search and we have exactly one match, select them
    if (activeListId && activeList && activeList.type !== 'table' && looksLikeName && filteredListGroups) {
      const allPeople = filteredListGroups.flatMap(g => g.people || []);
      if (allPeople.length === 1) {
        onSelectDonor && onSelectDonor(allPeople[0]);
        setSearchQuery('');
        return;
      }
      // If filtering is showing results, don't make API call
      if (allPeople.length > 0) {
        const totalPeople = listGroups?.flatMap(g => g.people || []).length || 0;
        if (allPeople.length < totalPeople) {
          return;
        }
      }
    }

    setIsSearchLoading(true);

    try {
      // Check if this is an "add [name]" request - should use universal chat, not organize-list
      const isAddRequest = /^add\s+.+/i.test(query);

      // If a list is active AND it's not an add request, use organize-list
      if (activeListId && activeList && !isAddRequest) {
        const response = await authPost('/api/organize-list', {
          query,
          mode: dataMode,
          groupId: activeGroupId
        });

        if (!response.ok) throw new Error('Failed to organize list');

        const data = await response.json();
        console.log('Organize list response:', data);

        // Update local list groups
        setListGroups(data.groups);

        // Save the updated list
        const updatedList = {
          ...activeList,
          organizationPrompt: query,
          groups: data.groups
        };

        if (onSaveList) {
          await onSaveList(updatedList, activeGroupId);
        }

        setSearchQuery('');
        setAiMessage(`Re-organized "${activeList.name}" by: ${query}`);
        setTimeout(() => setAiMessage(null), 2000);
        return;
      }

      // Normal search behavior when no list is active
      const response = await authPost('/api/search', {
        query,
        mode: dataMode,
        pipeId: 1,
        groupId: activeGroupId
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      console.log('Search response:', data);

      // Handle filter donors FIRST (show only specific donors)
      // BUT skip filtering if this is an add_donor action - the donor doesn't exist in local state yet
      if (data.filterDonors && data.filterDonors.length > 0 && data.action !== 'add_donor') {
        console.log('Filtering to:', data.filterDonors);
        const filtered = donors.filter(d => data.filterDonors.includes(d.name));
        console.log('Filtered result:', filtered.map(d => d.name));
        setFilteredDonors(filtered);
        setSearchQuery(''); // Clear search after filtering
      }

      // Handle reveal fields (show data on cards)
      if (data.revealFields && data.revealFields.length > 0) {
        const fieldMap = {
          'amount': 'amount',
          'lastContact': 'lastContact',
          'nextMeeting': 'nextMeeting',
          'sentiment': 'sentiment'
        };
        const field = fieldMap[data.revealFields[0]] || data.revealFields[0];
        console.log('Setting highlightField to:', field);
        setHighlightField(field);
        setSearchQuery(''); // Clear search after reveal
        // If no filter specified, make sure we show all donors
        if (!data.filterDonors) {
          setFilteredDonors(donors);
        }
      }

      // Handle actions like add_donor, save_note, move
      if (data.action === 'add_donor' || data.action === 'save_note' || data.action === 'move') {
        console.log('PeopleGrid action detected:', data.action, 'onAction exists:', !!onAction);
        // Call onAction to sync to Supabase
        if (onAction) {
          const actionPayload = {
            type: data.action,
            data: {
              name: data.donorName,
              amount: data.amount,
              notes: data.note || data.notes // Server returns 'note' (singular)
            }
          };
          console.log('PeopleGrid calling onAction with:', actionPayload);
          onAction(actionPayload);
        } else {
          console.log('PeopleGrid: onAction is not defined!');
        }

        // For add_donor, App.js opens the panel with initialData - don't open it here
        // For other actions (save_note, move), select the donor
        if (data.donorName && data.action !== 'add_donor') {
          onSelectDonor(data.donorName);
        }
        setSearchQuery('');
      }

      // Handle multiple donors added at once
      if (data.action === 'add_donors' && data.firstDonor) {
        console.log('PeopleGrid: Multiple donors added:', data.donors);
        // Open the first donor's panel
        onSelectDonor(data.firstDonor);
        setSearchQuery('');
        // Show confirmation message briefly
        if (data.content) {
          setAiMessage(data.content);
          setTimeout(() => setAiMessage(null), 2000);
        }
      }

      // Show AI message briefly and add to conversation history
      if (data.message) {
        setAiMessage(data.message);
        setTimeout(() => setAiMessage(null), 2000);
        if (onAddMessage) {
          onAddMessage({ role: 'user', content: query, timestamp: new Date().toISOString() });
          onAddMessage({ role: 'assistant', content: data.message, timestamp: new Date().toISOString() });
        }
      }

    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Get the highlighted value for a donor
  const getHighlightValue = (donor) => {
    if (!highlightField) return null;
    return donor[highlightField] || null;
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // Handle right-click on donor card
  const handleContextMenu = useCallback((e, donorName) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      donorName
    });
  }, []);

  // Handle delete donor
  const handleDeleteDonor = useCallback(async (donorName) => {
    if (onAction) {
      onAction({ type: 'delete_donor', data: { name: donorName } });
    }
    // Remove from local state immediately
    setDonors(prev => prev.filter(d => d.name !== donorName));
    setFilteredDonors(prev => prev.filter(d => d.name !== donorName));
    setDeleteConfirm(null);
    setContextMenu(null);
  }, [onAction]);

  // Handle file upload for import preview
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImportLoading(true);
    setAiMessage('Analyzing file...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch('/api/import-preview', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to analyze file');
      }

      const preview = await response.json();
      setImportPreview(preview);
      setAiMessage(null);
    } catch (error) {
      console.error('Import preview error:', error);
      setAiMessage(`Error: ${error.message}`);
    } finally {
      setIsImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle confirmed import
  const handleImportConfirm = async (contacts) => {
    setIsImporting(true);

    try {
      const response = await authPost('/api/import-confirm', { contacts, groupId: activeGroupId });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to import contacts');
      }

      const result = await response.json();

      // Refresh donor list
      if (dataMode === 'real' && user?.id) {
        const supabaseDonors = await fetchSupabaseDonors(user.id);
        const donorList = supabaseDonors.map(d => supabaseDonorToAppFormat(d));
        setDonors(donorList);
        setFilteredDonors(donorList);
      }

      setImportPreview(null);
      setAiMessage(`Successfully imported ${result.imported} contacts!`);

      // Clear success message after 5 seconds
      setTimeout(() => setAiMessage(null), 5000);
    } catch (error) {
      console.error('Import error:', error);
      setAiMessage(`Error: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Get current group name
  const activeGroup = groups.find(g => g.id === activeGroupId);

  // Helper functions for avatar
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPersonColor = (name) => {
    const colors = [
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#38bdf8', // sky blue
      '#a78bfa', // purple
      '#ec4899', // pink
      '#3b82f6', // blue
      '#14b8a6', // teal
      '#818cf8', // indigo
    ];
    const index = name ? name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length : 0;
    return colors[index];
  };

  // Helper to get group name for a donor (for "all people" view)
  const getGroupNameForDonor = (donor) => {
    if (!donor.groupId) return null;
    const group = groups.find(g => g.id === donor.groupId);
    return group ? group.name : null;
  };

  // Show group creation prompt only when NOT in showAllPeople mode and no group selected
  const showGroupCreationPrompt = !activeGroupId && !showAllPeople;

  // Show only group creation prompt when not viewing a specific group
  if (showGroupCreationPrompt) {
    return (
      <div className="people-grid-view create-mode">
        <div className="people-grid-create-group">
          <h2 className="create-group-title">Create a new Group</h2>
          <div className="create-group-input-wrapper">
            <input
              type="text"
              className="create-group-input"
              placeholder=""
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateGroupSubmit();
              }}
              disabled={isCreatingGroup}
            />
            {!newGroupName && (
              <div className={`create-group-example ${isAnimatingOut ? 'animating-out' : 'animating-in'}`}>
                {GROUP_EXAMPLE_PROMPTS[currentExampleIndex]}
              </div>
            )}
            {isCreatingGroup ? (
              <div className="create-group-spinner">
                <span></span><span></span><span></span>
              </div>
            ) : (
              <button className="create-input-submit" onClick={handleCreateGroupSubmit} disabled={!newGroupName.trim()}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="people-grid-view">
      {/* List creation overlay */}
      {showListCreation && (
        <div className="list-creation-overlay" onClick={() => setShowListCreation(false)}>
          <div className="list-creation-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="create-list-title">How would you like to view {activeGroup?.name}?</h2>
            <div className="create-list-input-wrapper">
              <input
                type="text"
                className="create-list-input"
                placeholder=""
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateListSubmit();
                  if (e.key === 'Escape') setShowListCreation(false);
                }}
                disabled={isCreatingList}
              />
              {!newListName && (
                <div className={`create-list-example ${isListAnimatingOut ? 'animating-out' : 'animating-in'}`}>
                  {LIST_EXAMPLE_PROMPTS[listExampleIndex]}
                </div>
              )}
              {isCreatingList ? (
                <div className="create-list-spinner">
                  <span></span><span></span><span></span>
                </div>
              ) : (
                <button className="create-input-submit" onClick={handleCreateListSubmit} disabled={!newListName.trim()}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table creation overlay */}
      {showTableCreation && (
        <div className="list-creation-overlay" onClick={() => setShowTableCreation(false)}>
          <div className="list-creation-modal table-creation" onClick={(e) => e.stopPropagation()}>
            <h2 className="create-list-title">What columns would you like in your table?</h2>
            <p className="create-table-subtitle">Describe the data you want to see. AI will extract it from your contacts' narratives.</p>
            <div className="create-list-input-wrapper">
              <input
                type="text"
                className="create-list-input"
                placeholder=""
                value={newTablePrompt}
                onChange={(e) => setNewTablePrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateTableSubmit();
                  if (e.key === 'Escape') setShowTableCreation(false);
                }}
                disabled={isCreatingTable}
              />
              {!newTablePrompt && (
                <div className={`create-list-example ${isTableAnimatingOut ? 'animating-out' : 'animating-in'}`}>
                  {TABLE_EXAMPLE_PROMPTS[tableExampleIndex]}
                </div>
              )}
              {isCreatingTable ? (
                <div className="create-list-spinner">
                  <span></span><span></span><span></span>
                </div>
              ) : (
                <button className="create-input-submit" onClick={handleCreateTableSubmit} disabled={!newTablePrompt.trim()}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="people-grid-header">
        {/* Row 1: All People header or Group name, Lists dropdown, and Import */}
        {showAllPeople && (
          <div className="people-grid-header-row">
            {/* Desktop: inline tabs */}
            <div className="people-grid-nav desktop-only">
              <div className="people-grid-group-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span
                  className={`group-name ${activeListId ? 'clickable' : 'active'}`}
                  onClick={activeListId ? () => onSelectList && onSelectList(null) : undefined}
                  title={activeListId ? 'Back to All People' : undefined}
                >
                  All
                </span>
              </div>
              {/* Inline view tabs (lists and tables) */}
              <div className="list-tabs">
                {allPeopleLists.map(view => (
                  <button
                    key={view.id}
                    className={`list-tab ${activeListId === view.id ? 'active' : ''} ${view.type === 'table' ? 'is-table-type' : ''}`}
                    onClick={() => onSelectList && onSelectList(view.id)}
                  >
                    {view.type === 'table' ? (
                      <svg className="view-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="3" y1="9" x2="21" y2="9" />
                        <line x1="3" y1="15" x2="21" y2="15" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                      </svg>
                    ) : (
                      <svg className="view-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    )}
                    <span className="list-tab-name" title={view.name}>{view.name}</span>
                    <span
                      className="list-tab-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmList(view);
                      }}
                    >
                      ×
                    </span>
                  </button>
                ))}
                <div className="view-type-dropdown-container">
                  <button
                    className="list-tab add-list-tab"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewTypeDropdown(!showViewTypeDropdown);
                    }}
                    title="Create new view"
                  >
                    +
                  </button>
                  {showViewTypeDropdown && (
                    <div className="view-type-dropdown">
                      <button
                        className="view-type-option"
                        onClick={() => {
                          setShowViewTypeDropdown(false);
                          setShowListCreation(true);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="8" y1="6" x2="21" y2="6" />
                          <line x1="8" y1="12" x2="21" y2="12" />
                          <line x1="8" y1="18" x2="21" y2="18" />
                          <line x1="3" y1="6" x2="3.01" y2="6" />
                          <line x1="3" y1="12" x2="3.01" y2="12" />
                          <line x1="3" y1="18" x2="3.01" y2="18" />
                        </svg>
                        New List
                      </button>
                      <button
                        className="view-type-option"
                        onClick={() => {
                          setShowViewTypeDropdown(false);
                          setShowTableCreation(true);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="3" y1="15" x2="21" y2="15" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                        </svg>
                        New Table
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Mobile: selector that opens sheet */}
            <button
              className="mobile-list-selector mobile-only"
              onClick={() => setShowAllPeopleSheet(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="mobile-list-selector-text">
                {activeListId && activeList ? activeList.name : 'All'}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".csv,.txt,.json"
              style={{ display: 'none' }}
            />
            <div className="add-dropdown-container">
              <button
                className="add-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddDropdown(!showAddDropdown);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showAddDropdown && (
                <div className="add-dropdown-menu">
                  <button
                    className="add-dropdown-item"
                    onClick={() => {
                      setShowAddDropdown(false);
                      onAddPerson();
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                      <line x1="12" y1="11" x2="12" y2="17" />
                      <line x1="9" y1="14" x2="15" y2="14" />
                    </svg>
                    Add Person
                  </button>
                  <button
                    className="add-dropdown-item"
                    onClick={() => {
                      setShowAddDropdown(false);
                      fileInputRef.current?.click();
                    }}
                    disabled={isImportLoading}
                  >
                    {isImportLoading ? (
                      <span className="import-spinner"></span>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                    )}
                    Import
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {activeGroup && (
          <>
          <div className="people-grid-header-row">
            {/* Desktop: inline tabs */}
            <div className="people-grid-nav desktop-only">
              <div className="people-grid-group-header">
                <span className="group-indicator" style={{ backgroundColor: activeGroup.color }}></span>
                <span
                  className={`group-name ${activeListId ? 'clickable' : 'active'}`}
                  onClick={activeListId ? () => onSelectList && onSelectList(null) : undefined}
                  title={activeListId ? `Back to all ${activeGroup.name}` : undefined}
                >
                  {activeGroup.name}
                </span>
              </div>
              {/* Inline view tabs (lists and tables) */}
              <div className="list-tabs">
                {groupLists.map(view => (
                  <button
                    key={view.id}
                    className={`list-tab ${activeListId === view.id ? 'active' : ''} ${view.type === 'table' ? 'is-table-type' : ''}`}
                    onClick={() => onSelectList && onSelectList(view.id)}
                  >
                    {view.type === 'table' ? (
                      <svg className="view-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="3" y1="9" x2="21" y2="9" />
                        <line x1="3" y1="15" x2="21" y2="15" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                      </svg>
                    ) : (
                      <svg className="view-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    )}
                    <span className="list-tab-name" title={view.name}>{view.name}</span>
                    <span
                      className="list-tab-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmList(view);
                      }}
                    >
                      ×
                    </span>
                  </button>
                ))}
                <div className="view-type-dropdown-container">
                  <button
                    className={`list-tab add-list-tab ${filteredDonors.length >= 2 && groupLists.length === 0 && !dismissedHints['views-hint'] ? 'hint-highlight' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowViewTypeDropdown(!showViewTypeDropdown);
                    }}
                    title="Create new view"
                  >
                    +
                  </button>
                  {showViewTypeDropdown && (
                    <div className="view-type-dropdown">
                      <button
                        className="view-type-option"
                        onClick={() => {
                          setShowViewTypeDropdown(false);
                          setShowListCreation(true);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="8" y1="6" x2="21" y2="6" />
                          <line x1="8" y1="12" x2="21" y2="12" />
                          <line x1="8" y1="18" x2="21" y2="18" />
                          <line x1="3" y1="6" x2="3.01" y2="6" />
                          <line x1="3" y1="12" x2="3.01" y2="12" />
                          <line x1="3" y1="18" x2="3.01" y2="18" />
                        </svg>
                        New List
                      </button>
                      <button
                        className="view-type-option"
                        onClick={() => {
                          setShowViewTypeDropdown(false);
                          setShowTableCreation(true);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="3" y1="15" x2="21" y2="15" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                        </svg>
                        New Table
                      </button>
                    </div>
                  )}
                  {/* Hint for creating lists/tables */}
                  {filteredDonors.length >= 2 && groupLists.length === 0 && !dismissedHints['views-hint'] && (
                    <div className="views-hint-popover">
                      <div className="views-hint-arrow"></div>
                      <div className="views-hint-content">
                        <span className="views-hint-text">Create Lists or Tables to extract data</span>
                        <button
                          className="views-hint-dismiss"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissHint('views-hint');
                          }}
                          aria-label="Dismiss"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Mobile: collapsible selector */}
            <div className="mobile-list-selector-wrapper mobile-only">
              <button
                className={`mobile-list-selector ${filteredDonors.length >= 2 && groupLists.length === 0 && !dismissedHints['views-hint-mobile'] ? 'hint-highlight' : ''}`}
                onClick={() => setShowMobileListSheet(true)}
              >
                <span className="group-indicator" style={{ backgroundColor: activeGroup.color }}></span>
                <span className="mobile-list-selector-text">
                  {activeListId && activeList ? activeList.name : activeGroup.name}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {/* Mobile hint for context (shows first if no context) */}
              {!activeGroup.context && !dismissedHints['context-hint-mobile'] && (
                <div className="mobile-context-hint-popover">
                  <div className="mobile-context-hint-arrow"></div>
                  <div className="mobile-context-hint-content">
                    <span className="mobile-context-hint-text">Add context to help AI understand this group</span>
                    <button
                      className="mobile-context-hint-dismiss"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissHint('context-hint-mobile');
                      }}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              {/* Mobile hint for lists/tables (shows after context is set or dismissed) */}
              {(activeGroup.context || dismissedHints['context-hint-mobile']) && filteredDonors.length >= 2 && groupLists.length === 0 && !dismissedHints['views-hint-mobile'] && (
                <div className="mobile-views-hint-popover">
                  <div className="mobile-views-hint-arrow"></div>
                  <div className="mobile-views-hint-content">
                    <span className="mobile-views-hint-text">Create Lists or Tables to extract data</span>
                    <button
                      className="mobile-views-hint-dismiss"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissHint('views-hint-mobile');
                      }}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv,.txt,.json"
              style={{ display: 'none' }}
            />
            <div className="add-dropdown-container">
              <button
                className="add-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddDropdown(!showAddDropdown);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showAddDropdown && (
                <div className="add-dropdown-menu">
                  <button
                    className="add-dropdown-item"
                    onClick={() => {
                      setShowAddDropdown(false);
                      onAddPerson();
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                      <line x1="12" y1="11" x2="12" y2="17" />
                      <line x1="9" y1="14" x2="15" y2="14" />
                    </svg>
                    Add Person
                  </button>
                  <button
                    className="add-dropdown-item"
                    onClick={() => {
                      setShowAddDropdown(false);
                      fileInputRef.current?.click();
                    }}
                    disabled={isImportLoading}
                  >
                    {isImportLoading ? (
                      <span className="import-spinner"></span>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                    )}
                    Import
                  </button>
                </div>
              )}
            </div>
            <div className="context-btn-wrapper">
              <button
                className={`context-btn desktop-only ${!activeGroup.context && !dismissedHints[`group-context-${activeGroup.id}`] ? 'hint-highlight' : ''}`}
                onClick={() => setShowEditGroupModal(true)}
                title={activeGroup.context ? "View/edit group context" : "Add context for AI"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                Context
              </button>

              {/* Contextual hint popover for empty group context */}
              {!activeGroup.context && !dismissedHints[`group-context-${activeGroup.id}`] && (
                <div className="context-hint-popover">
                  <div className="context-hint-arrow"></div>
                  <div className="context-hint-content">
                    <span className="context-hint-text">Add context to help AI understand this group's purpose</span>
                    <button
                      className="context-hint-dismiss"
                      onClick={() => dismissHint(`group-context-${activeGroup.id}`)}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {/* Row 2: Search bar */}
        <div className="people-grid-header-row search-row">
          <div className="people-grid-search full-width">
            <SearchBox
              placeholder={activeListId ? (activeList?.type === 'table' ? "Re-organize this table..." : "Re-organize this list...") : "Search or ask about contacts..."}
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              loading={isSearchLoading}
              expandable={true}
              rainbowPlaceholder={!activeListId}
            />
          </div>
        </div>
      </div>

      {/* AI Response message */}
      {aiMessage && (
        <div className="people-grid-message">{aiMessage}</div>
      )}

      {isLoading ? (
        <div className="people-grid-loading">
          <div className="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      ) : activeList && activeList.type === 'table' ? (
        /* Show table view when a table is selected */
        <div className="table-view-container">
          {/* Desktop: Traditional table */}
          <table className="table-view desktop-only">
            <thead>
              <tr>
                {(activeList.columns || []).map((col, i) => (
                  <th
                    key={i}
                    className={`${col !== 'Name' ? 'editable-header' : ''} ${populatingColumn === col ? 'populating' : ''} ${editingColumnIndex === i ? 'editing' : ''}`}
                    onClick={() => handleColumnClick(i, col)}
                  >
                    <span className="column-header-text">{col}</span>
                    {editingColumnIndex === i && (
                      <>
                        <input
                          type="text"
                          className="column-header-input"
                          value={editingColumnName}
                          onChange={(e) => setEditingColumnName(e.target.value)}
                          onKeyDown={handleColumnKeyDown}
                          onBlur={(e) => {
                            // Don't save if clicking on delete button
                            if (e.relatedTarget?.classList?.contains('column-delete-btn')) return;
                            saveColumnEdit();
                          }}
                          autoFocus
                        />
                        <button
                          className="column-delete-btn"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteColumn(i, col);
                          }}
                          title="Remove column"
                        >
                          ×
                        </button>
                      </>
                    )}
                  </th>
                ))}
                <th className="add-column-header">
                  {isAddingColumn ? (
                    <input
                      type="text"
                      className="column-header-input new-column-input"
                      placeholder="Column name..."
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddColumnSubmit();
                        if (e.key === 'Escape') {
                          setIsAddingColumn(false);
                          setNewColumnName('');
                        }
                      }}
                      onBlur={() => {
                        if (!newColumnName.trim()) {
                          setIsAddingColumn(false);
                          setNewColumnName('');
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      className="add-column-btn"
                      onClick={() => setIsAddingColumn(true)}
                      title="Add column"
                    >
                      +
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTableRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="table-row"
                  style={{ '--row-index': rowIndex }}
                >
                  {(activeList.columns || []).map((col, colIndex) => (
                    <td
                      key={colIndex}
                      className={`table-cell ${col !== 'Name' ? 'editable' : ''} ${editingCell?.rowIndex === rowIndex && editingCell?.colName === col ? 'editing' : ''} ${updatingCell?.rowIndex === rowIndex && updatingCell?.colName === col ? 'updating' : ''} ${populatingColumn === col ? 'populating' : ''}`}
                      onClick={(e) => col === 'Name'
                        ? (row.Name && onSelectDonor && onSelectDonor(row.Name))
                        : handleCellClick(e, rowIndex, col, row.Name, row[col])
                      }
                    >
                      <span className="table-cell-content">{row[col] || ''}</span>
                      {editingCell?.rowIndex === rowIndex && editingCell?.colName === col && (
                        <input
                          type="text"
                          className="table-cell-input"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={handleCellKeyDown}
                          onBlur={handleCellBlur}
                          autoFocus
                        />
                      )}
                    </td>
                  ))}
                  <td className="add-column-spacer"></td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile: Card view */}
          <div className="table-cards mobile-only">
            {filteredTableRows.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="table-card"
                style={{ '--row-index': rowIndex }}
                onClick={() => row.Name && onSelectDonor && onSelectDonor(row.Name)}
              >
                <div className="table-card-name">{row.Name || 'Unknown'}</div>
                <div className="table-card-fields">
                  {(activeList.columns || []).filter(col => col !== 'Name').map((col, colIndex) => (
                    <div key={colIndex} className="table-card-field">
                      <span className="table-card-label">{col}</span>
                      <span className="table-card-value">{row[col] || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {filteredTableRows.length === 0 && !searchQuery && (
            <div className="table-empty">No data yet. Add people to this group to see table data.</div>
          )}
          {filteredTableRows.length === 0 && searchQuery && (
            <div className="table-empty">No matches found for "{searchQuery}"</div>
          )}
        </div>
      ) : filteredListGroups && filteredListGroups.length > 0 ? (
        /* Show organized list view when a list is selected */
        <div className="list-view-inline">
          <div className="list-groups rows">
            {filteredListGroups.map((group, index) => (
              <div
                key={index}
                className={`list-group ${dropTargetGroup === index ? 'drop-target' : ''}`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <h3 className="list-group-name">{group.name}</h3>
                <div className="list-group-people">
                  {(group.people || []).map((person, personIndex) => (
                    <button
                      key={personIndex}
                      className={`list-person ${draggedPerson === person ? 'dragging' : ''}`}
                      onClick={() => onSelectDonor && onSelectDonor(person)}
                      draggable
                      onDragStart={() => handleDragStart(person, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {person}
                    </button>
                  ))}
                  {(!group.people || group.people.length === 0) && !searchQuery && (
                    <span className="list-group-empty">Drop someone here</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {listGroups && listGroups.length > 0 && filteredListGroups.length === 0 && searchQuery && (
            <div className="list-empty-search">No matches found for "{searchQuery}"</div>
          )}
        </div>
      ) : filteredDonors.length === 0 && !searchQuery ? (
        <div className="people-grid-empty">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <p className="empty-state-title">
            {showAllPeople ? "No one's here yet" : `No one in ${activeGroup?.name || 'this group'} yet`}
          </p>
          <p className="empty-state-hint">
            Add someone using the button above, or type in the main chat:
          </p>
          <p className="empty-state-example">
            "Add Sarah Chen, met her at the gala"
          </p>
          <button className="people-grid-add-btn" onClick={onAddPerson}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add your first person
          </button>
        </div>
      ) : filteredDonors.length === 0 ? (
        null
      ) : (
        <div className={`people-grid ${showAllPeople ? 'all-people-view' : ''}`}>
          {filteredDonors.map((donor, index) => {
            const highlightValue = getHighlightValue(donor);
            const groupName = showAllPeople ? getGroupNameForDonor(donor) : null;
            return (
              <button
                key={donor.name}
                className={`donor-card ${highlightValue ? 'has-highlight' : ''} ${showAllPeople ? 'show-group' : ''}`}
                onClick={() => onSelectDonor(donor.name)}
                onContextMenu={(e) => handleContextMenu(e, donor.name)}
                style={{ '--card-index': index }}
                title={groupName ? `Group: ${groupName}` : undefined}
              >
                <div
                  className="donor-card-avatar"
                  style={{ borderColor: getPersonColor(donor.name), color: getPersonColor(donor.name) }}
                >
                  {getInitials(donor.name)}
                </div>
                <div className="donor-card-info">
                  <span className="donor-card-name">{donor.name}</span>
                  {highlightValue && (
                    <span className="donor-card-highlight">{highlightValue}</span>
                  )}
                  {groupName && (
                    <span className="donor-card-group">{groupName}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="donor-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="context-menu-item delete"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm(contextMenu.donorName);
              setContextMenu(null);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            Delete Donor
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Donor</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm}</strong>?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="delete-btn" onClick={() => handleDeleteDonor(deleteConfirm)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {importPreview && (
        <ImportPreview
          preview={importPreview}
          onConfirm={handleImportConfirm}
          onCancel={() => setImportPreview(null)}
          isImporting={isImporting}
        />
      )}

      {/* Delete List Confirmation Modal */}
      {deleteConfirmList && (
        <div className="list-delete-overlay" onClick={() => setDeleteConfirmList(null)}>
          <div className="list-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete List</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirmList.name}</strong>?</p>
            <div className="list-delete-actions">
              <button
                className="list-delete-cancel"
                onClick={() => setDeleteConfirmList(null)}
              >
                Cancel
              </button>
              <button
                className="list-delete-confirm"
                onClick={() => {
                  if (onDeleteList) onDeleteList(deleteConfirmList.id);
                  setDeleteConfirmList(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile List Selection Sheet */}
      {showMobileListSheet && activeGroup && (
        <div className="mobile-sheet-overlay" onClick={() => setShowMobileListSheet(false)}>
          <div className="mobile-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle"></div>
            <div className="mobile-sheet-options">
              <button
                className={`mobile-sheet-option ${!activeListId ? 'active' : ''}`}
                onClick={() => {
                  onSelectList && onSelectList(null);
                  setShowMobileListSheet(false);
                }}
              >
                <span className="group-indicator" style={{ backgroundColor: activeGroup.color }}></span>
                All {activeGroup.name}
              </button>
              <button
                className={`mobile-sheet-option context-option ${!activeGroup.context && !dismissedHints['context-hint-mobile'] ? 'hint-highlight' : ''}`}
                onClick={() => {
                  setShowMobileListSheet(false);
                  setShowEditGroupModal(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Context
              </button>
              {groupLists.map(list => (
                <div key={list.id} className="mobile-sheet-option-row">
                  <button
                    className={`mobile-sheet-option ${activeListId === list.id ? 'active' : ''}`}
                    onClick={() => {
                      onSelectList && onSelectList(list.id);
                      setShowMobileListSheet(false);
                    }}
                  >
                    {list.type === 'table' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="3" y1="9" x2="21" y2="9" />
                        <line x1="3" y1="15" x2="21" y2="15" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    )}
                    {list.name}
                  </button>
                  <button
                    className="mobile-sheet-delete"
                    onClick={() => {
                      setShowMobileListSheet(false);
                      setDeleteConfirmList(list);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="mobile-sheet-actions">
              <button
                className="mobile-sheet-add"
                onClick={() => {
                  setShowMobileListSheet(false);
                  setShowListCreation(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                New List
              </button>
              <button
                className="mobile-sheet-add"
                onClick={() => {
                  setShowMobileListSheet(false);
                  setShowTableCreation(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
                New Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile All People Selection Sheet */}
      {showAllPeopleSheet && showAllPeople && (
        <div className="mobile-sheet-overlay" onClick={() => setShowAllPeopleSheet(false)}>
          <div className="mobile-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle"></div>
            <div className="mobile-sheet-header">
              <span className="group-indicator" style={{ backgroundColor: '#8b5cf6' }}></span>
              <span>All</span>
            </div>
            <div className="mobile-sheet-options">
              <button
                className={`mobile-sheet-option ${!activeListId ? 'active' : ''}`}
                onClick={() => {
                  onSelectList && onSelectList(null);
                  setShowAllPeopleSheet(false);
                }}
              >
                All People
              </button>
              {allPeopleLists.map(list => (
                <div key={list.id} className="mobile-sheet-option-row">
                  <button
                    className={`mobile-sheet-option ${activeListId === list.id ? 'active' : ''}`}
                    onClick={() => {
                      onSelectList && onSelectList(list.id);
                      setShowAllPeopleSheet(false);
                    }}
                  >
                    {list.type === 'table' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="3" y1="9" x2="21" y2="9" />
                        <line x1="3" y1="15" x2="21" y2="15" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    )}
                    {list.name}
                  </button>
                  <button
                    className="mobile-sheet-delete"
                    onClick={() => {
                      setShowAllPeopleSheet(false);
                      setDeleteConfirmList(list);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="mobile-sheet-actions">
              <button
                className="mobile-sheet-add"
                onClick={() => {
                  setShowAllPeopleSheet(false);
                  setShowListCreation(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                New List
              </button>
              <button
                className="mobile-sheet-add"
                onClick={() => {
                  setShowAllPeopleSheet(false);
                  setShowTableCreation(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
                New Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      <EditGroupModal
        isOpen={showEditGroupModal}
        onClose={() => setShowEditGroupModal(false)}
        group={activeGroup}
        onUpdateGroup={onUpdateGroup}
      />
    </div>
  );
}

export default PeopleGrid;
